/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useEffect, memo, useCallback, useRef } from 'react';
import Style from './../css/PaymentsAndDisbursements.module.css';
import LoadingModal from '../components/modals/LoadingModal';
import AddIcon from '@mui/icons-material/Add';
import {
  TableRow,
  TableCell,
  TextField,
  Table,
  TableHead,
  TableBody,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { LoanPaymentRowType } from '../types/LoanPaymentRowType';
import type { QidType } from '../types/QidType';
import LoanPaymentRow from './LoanPaymentRow';
import { useSnackbar } from 'notistack';
import useAbortController from '../common/hooks/useAbortController';
import axiosCall from '../services/axios';
import { convertDateInputTo8AMUTC, dateToEpoch } from '../common/UtilityFunctions';
import {
  ASSESSMENT_TRANSACTIONS,
  CHECK_NUMBER_TRANSACTIONS,
  SALE_TRANSFER_TRANSACTIONS,
  REASON_TRANSACTIONS,
  MORTGAGE_PAYMENT_TRANSACTIONS,
  CORPORATE_ADVANCE_TRANSACTIONS,
  ESCROW_TRANSACTIONS,
  FEE_CNT_TRANSACTIONS,
  FEE_NO_TRANSACTIONS,
  IMPOUND_TRANSACTIONS,
  INTEREST_TRANSACTIONS,
  LATE_CHARGE_TRANSACTIONS,
  PAYEE2_TRANSACTIONS,
  PAYEE3_TRANSACTIONS,
  PAYEE_TRANSACTIONS,
  PAYMENT_AMOUNT_TRANSACTIONS,
  PAYMENT_RECEIVED_DATE_TRANSACTIONS,
  PRINCIPAL_CURTAIL_TRANSACTIONS,
  PRINCIPAL_TRANSACTIONS,
  SUFFIX_TRANSACTIONS,
  SUSPENSE_TRANSACTIONS,
  UNIT_NO_TRANSACTIONS,
  ALL_TRANSACTIONS,
  TRANSACTION_PROPERTIES,
  TOTAL_TRANSACTIONS,
  TAX_DISBURSEMENT_CODES,
  HAZARD_INSURANCE_CODES,
  MORTGAGE_INSURANCE_PREMIUM_CODES,
  HOA_DISBURSEMENT_CODES,
  GROUND_RENT_DISBURSEMENT_CODES,
  LOAN_PAYMENT_CODES,
  ROW_TOTAL_TRANSACTIONS,
  TR_TYPE_TRANSACTIONS,
  EXCESS_FUNDS_TRANSACTIONS,
  SUBSIDY_TRANSACTIONS,
} from '../contracts/constants';
import dayjs from 'dayjs';

type loanPaymentTableProps = {
  transactionCode: string;
  suffixQidList: QidType[];
  transactionQidList: QidType[];
  batchNumber: string;
  token: string;
  // Judith Espinal - 04/20/2026 - SQC-557 Issue 1: supplied by parent useGetBatchServPayments (was fetched inside this component before).
  payments: any[];
  loadingBatch: boolean;
  batchErrorMessages: string;
};

const LoanPaymentTable = memo((props: loanPaymentTableProps) => {
  const { get } = useAbortController();
  const { enqueueSnackbar } = useSnackbar();
  const [hasUpdates, setHasUpdates] = useState(false);
  const [nextId, setNextId] = useState(0);
  const [rows, setRows] = useState([] as LoanPaymentRowType[]);
  const [total, setTotal] = useState(0.0);
  const [disableSave, setDisableSave] = useState(false);
  // Judith Espinal - 04/20/2026 - SQC-557 Issue 2: default date for bulk apply toolbar (same dayjs semantics as row DatePicker).
  const [bulkPaymentDate, setBulkPaymentDate] = useState<dayjs.Dayjs | null>(() => dayjs());
  // Stays checked after a successful apply; cleared when the bulk date changes or the user unchecks.
  const [bulkApplyToAllChecked, setBulkApplyToAllChecked] = useState(false);
  const [postingRowId, setPostingRowId] = useState<number | null>(null);
  const { payments, batchErrorMessages, loadingBatch } = props;

  // sets dateReceived on every row; per-row date fields are disabled while bulk "Apply to all" stays checked.
  const applyPaymentReceivedDateToAllRows = useCallback((): boolean => {
    if (!bulkPaymentDate?.isValid()) {
      enqueueSnackbar('Select a valid payment received date.', { variant: 'warning' });
      return false;
    }
    const unix = bulkPaymentDate.unix();
    setRows((prev) => prev.map((row) => ({ ...row, dateReceived: unix })));
    enqueueSnackbar('Payment received date applied to all rows.', { variant: 'success' });
    return true;
  }, [bulkPaymentDate, enqueueSnackbar]);

  // Delete all transactions from a batch
  const deleteBatch = async () => {
    for await (const row of rows) {
      await removeRow(row.total, row.cnt, row.id);
    }
  };

  // Add a new row to the batch. Initialize fields based on transaction type
  const addNewRow = useCallback(
    (item) => {
      let newRow: LoanPaymentRowType = {
        id: nextId,
        loan: '',
        mcdTransType:
          props.transactionCode === 'TX' ||
          props.transactionCode === 'HA' ||
          props.transactionCode === 'GR' ||
          props.transactionCode === 'HZ' ||
          props.transactionCode === 'MI' ||
          props.transactionCode === 'MP'
            ? ''
            : props.transactionCode,
        cnt: '',
        mcdTransSuffix: '',
        dateReceived: dateToEpoch(convertDateInputTo8AMUTC(dayjs().format('YYYY-MM-DD'))),
        description: '',
        amount: '0',
        checkNo: '',
        lateChargeReqstd: '0',
        from: '',
        to: '',
        groupCode: '',
        principalReqstd: '0',
        interestReqstd: '0',
        extraEscrowReqstd: '0',
        assessmentsReqstd: '0',
        corpAdvanceReqstd: '0',
        suspenseReqstd: '0',
        prinCurtailReqstd: '0',
        reason: '',
        total: '0',
        assessorCode: '',
        payee: '',
        dueDate: '',
        parcelId: '',
        impGroup: '',
        impType: '',
        renewalDate: '',
        unitNo: '',
        policyNo: '',
        payeeTwo: '',
        payeeThree: '',
        feeNo: '',
        feeCnt: '',
        rvrsBatch: '',
        rvrsCnt: '',
        errors: '',
        ownerXferDate: null,
        subsidyName: '',
        subsidyCnt: '',
      } as LoanPaymentRowType;

      setRows((prev) => [...prev, newRow]);
      setNextId((prev) => prev + 1);
    },
    [nextId, props.transactionCode],
  );

  // Remove a single row from the batch
  const removeRow = useCallback(
    async (rowTotal: string, cnt: string, id: number) => {
      if (!Boolean(cnt)) {
        setRows((prev) => prev.filter((row) => row.id !== id));
        setTotal((prev) => prev - parseFloat(rowTotal));
      } else {
        const url = process.env.REACT_APP_POST_TRANSACTION.replace('?', props.batchNumber) + cnt;
        const controller = get();
        const config = { signal: controller.signal };

        const formData = {
          active: false,
        };

        const headers = {
          'Session-Token': props.token,
          'Content-Type': 'application/json',
        };

        try {
          const response = await axiosCall({ url: url, method: 'post', data: formData }, config, headers);

          enqueueSnackbar(`Transaction deleted ${cnt}`, { variant: 'success' });
          setRows((prev) => prev.filter((row) => row.id !== id));
          setTotal((prev) => prev - parseFloat(rowTotal));
          return response;
        } catch (error) {
          console.error('Error Deleting Transaction: ', error);
          enqueueSnackbar(`Error Deleting Transaction: ${error.response.data.message}`, { variant: 'error' });
        }

        return null;
      }
    },
    [props.batchNumber, props.token, get, setRows, enqueueSnackbar],
  );

  // Update a row in the batch
  const updateRow = useCallback(
    (rowId: number, update: any) => {
      console.log(update);
      setRows((prev) => {
        let newTotal = 0;
        const newRows = prev.map((item) => {
          let subtotal = 0;

          if (item.id !== rowId) {
            newTotal += parseFloat(item.total);
            return item;
          } else {
            if (PAYMENT_AMOUNT_TRANSACTIONS.includes(item.mcdTransType)) {
              if ('amount' in update) {
                subtotal += parseFloat(update.amount || '0') || 0;
              } else {
                subtotal += parseFloat(item.amount || '0') || 0;
              }

              // suspense will be negative but should be added as a positive to the total of the row
              if (
                SUSPENSE_TRANSACTIONS.includes(item.mcdTransType) &&
                !MORTGAGE_PAYMENT_TRANSACTIONS.includes(item.mcdTransType)
              ) {
                if ('suspenseReqstd' in update) {
                  subtotal += Math.abs(parseFloat(update.suspenseReqstd || '0')) || 0;
                } else {
                  subtotal += Math.abs(parseFloat(item.suspenseReqstd || '0')) || 0;
                }
              }
            } else {
              for (const [key, properties] of Object.entries(TRANSACTION_PROPERTIES)) {
                if (ALL_TRANSACTIONS[key].includes(props.transactionCode)) {
                  for (const property of Object.keys(properties)) {
                    if (!TOTAL_TRANSACTIONS.includes(key)) continue;
                    if (property in update) {
                      subtotal += parseFloat(update[property] || '0') || 0;
                    } else {
                      subtotal += parseFloat(item[property] || '0') || 0;
                    }
                  }
                }
              }
            }

            item = {
              ...item,
              ...update,
              total: subtotal.toString(),
            };
            newTotal += subtotal;
            return item;
          }
        });

        setTotal(newTotal);
        return newRows;
      });
    },
    [props.transactionCode],
  );

  // Initialize rows based on row types and transaction data
  const setUpRows = useCallback(() => {
    setNextId((prev) => {
      const paymentRows = [];
      let count = prev;
      let subtotal = 0;

      payments.forEach((item) => {
        let total = 0;
        const mcdTransTypeTrimmed = String(item.mcdTransType ?? '').trim();
        const isTransactionCodeValid =
          mcdTransTypeTrimmed === props.transactionCode ||
          (props.transactionCode === 'TX' && TAX_DISBURSEMENT_CODES.includes(mcdTransTypeTrimmed)) ||
          (props.transactionCode === 'HA' &&
            (HOA_DISBURSEMENT_CODES.includes(mcdTransTypeTrimmed) || item.impGroup === 'HA')) ||
          (props.transactionCode === 'GR' &&
            (GROUND_RENT_DISBURSEMENT_CODES.includes(mcdTransTypeTrimmed) || item.impGroup === 'GR')) ||
          (props.transactionCode === 'HZ' && HAZARD_INSURANCE_CODES.includes(mcdTransTypeTrimmed)) ||
          (props.transactionCode === 'MI' && MORTGAGE_INSURANCE_PREMIUM_CODES.includes(mcdTransTypeTrimmed)) ||
          (props.transactionCode === 'MP' && LOAN_PAYMENT_CODES.includes(mcdTransTypeTrimmed));

        if (isTransactionCodeValid && !item.posted && item.active) {
          let newRow: LoanPaymentRowType = {
            id: count,
            loan: item.loan,
            mcdTransType: mcdTransTypeTrimmed,
            cnt: item.cnt,
            errors: '',
          } as LoanPaymentRowType;

          // Set properties based on TRANSACTION_PROPERTIES
          for (const [key, properties] of Object.entries(TRANSACTION_PROPERTIES)) {
            if (ALL_TRANSACTIONS[key].includes(props.transactionCode)) {
              for (const [property, valueFunction] of Object.entries(properties)) {
                newRow[property] = valueFunction(item);
                // Accumulate total if it's a relevant transaction type
                if (!TOTAL_TRANSACTIONS.includes(key)) continue;
                if (key === 'PAYMENT_AMOUNT_TRANSACTIONS') {
                  total += parseFloat(newRow[property] || '0') || 0;
                }

                if (PAYMENT_AMOUNT_TRANSACTIONS.includes(props.transactionCode)) {
                  if (key === 'SUSPENSE_TRANSACTIONS') {
                    total += Math.abs(parseFloat(newRow[property] || '0')) || 0;
                  }
                  continue;
                }

                total += parseFloat(newRow[property] || '0') || 0;
              }
            }
          }

          newRow['total'] = total.toString();
          subtotal += total;
          count++;
          paymentRows.push(newRow);
        }
      });

      setRows(paymentRows);
      setTotal(subtotal);
      return count;
    });
  }, [payments, props.transactionCode]);

  // post payoff transactions and save the PIFReason simultaneously using the batch endpoint
  const postPayoff = useCallback(
    async (reason: string, loanNumber: string, count: string) => {
      if (!loanNumber || !count) return;

      const url = `${process.env.REACT_APP_BATCH_REQUEST}`;
      const controller = get();
      const config = { sign: controller.signal };

      const formData = {
        PIFReason: {
          endpoint: `loan/${loanNumber}`,
          data: {
            PIFReason: reason,
          },
        },
        PostTransaction: {
          endpoint: `servicing/payments/batch/${props.batchNumber}/cnt/${count}`,
          data: {
            posted: true,
          },
        },
      };

      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };

      try {
        const response = await axiosCall({ url, method: 'post', data: formData }, config, headers);
        return response;
      } catch (error) {
        console.error(error.message);
      }
    },
    [props.batchNumber, props.token, get],
  );

  // Create a new transaction in the batch
  const createTransaction = useCallback(
    async (row: LoanPaymentRowType) => {
      if (!row.loan || row.loan.length !== 12) {
        enqueueSnackbar('Invalid loan number for row: ' + row.id, {
          variant: 'error',
        });
        return;
      }

      const data: Record<string, any> = { mcdTransType: row.mcdTransType };

      // Loop through ALL_TRANSACTIONS to build data
      for (const transactionKey of Object.keys(ALL_TRANSACTIONS)) {
        if (!ALL_TRANSACTIONS[transactionKey].includes(props.transactionCode)) continue;
        const propertyMapping = TRANSACTION_PROPERTIES[transactionKey];
        if (!propertyMapping) continue;
        for (const [propertyKey, valueFunction] of Object.entries(propertyMapping)) {
          if (typeof valueFunction !== 'function') {
            data[propertyKey] = valueFunction;
          } else {
            const value = valueFunction(row);
            if (value !== undefined) {
              data[propertyKey] = value;
            }
          }
        }
      }

      const request = {
        url: `${process.env.REACT_APP_CREATE_TRANSACTION.replace('?', props.batchNumber)}${row.loan}/add`,
        method: 'post',
        data,
      };
      const config = get();
      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };

      try {
        const response = await axiosCall(request, config, headers);
        enqueueSnackbar('Transaction created: ' + response.cnt, {
          variant: 'success',
        });
        return response;
      } catch (error) {
        console.error('Error Creating new Transaction: ' + error.response.data.message);
        enqueueSnackbar('Error Creating new Transaction. Hover icon in column 2', { variant: 'error' });
        return { errors: error.response.data.message };
      }
    },
    [props.batchNumber, props.token, enqueueSnackbar, get, props.transactionCode],
  );

  // Save changes to an existing transaction in the batch
  const saveTransaction = useCallback(
    async (row: LoanPaymentRowType) => {
      const url = process.env.REACT_APP_POST_TRANSACTION.replace('?', props.batchNumber) + row.cnt;

      const controller = get();
      const config = { signal: controller.signal };

      const data: Record<string, any> = { mcdTransType: row.mcdTransType };

      // Loop through ALL_TRANSACTIONS to build data
      for (const transactionKey of Object.keys(ALL_TRANSACTIONS)) {
        if (!ALL_TRANSACTIONS[transactionKey].includes(props.transactionCode)) continue;
        const propertyMapping = TRANSACTION_PROPERTIES[transactionKey];
        if (!propertyMapping) continue;
        for (const [propertyKey, valueFunction] of Object.entries(propertyMapping)) {
          if (typeof valueFunction !== 'function') {
            data[propertyKey] = valueFunction;
          } else {
            const value = valueFunction(row);
            if (value !== undefined) {
              data[propertyKey] = value;
            }
          }
        }
      }

      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };

      try {
        const response = await axiosCall({ url: url, method: 'post', data: data }, config, headers);
        enqueueSnackbar('Transaction Saved ' + row.cnt, { variant: 'success' });
        return response;
      } catch (error) {
        console.error('Error Saving Transaction: ' + error.response.data.message);
        enqueueSnackbar('Error Saving Transaction. Hover icon in column 2', {
          variant: 'error',
        });
        return { errors: error.response.data.message };
      }
    },
    [enqueueSnackbar, get, props.batchNumber, props.token, props.transactionCode],
  );

  // Post a transaction in the batch
  const postTransaction = useCallback(
    async (count: string, row: LoanPaymentRowType) => {
      // Validate that the count and loan number are provided
      if (!count || !row.loan) {
        enqueueSnackbar('Count or loan number is missing.', {
          variant: 'error',
        });
        return null;
      }

      const url = `${process.env.REACT_APP_POST_TRANSACTION.replace('?', props.batchNumber)}${count}`;

      const controller = get();
      const config = { signal: controller.signal };

      // Define formData with type annotation
      const formData: { posted: boolean } = {
        posted: true,
      };

      const headers = {
        'Session-Token': props.token,
        'Content-Type': 'application/json',
      };

      try {
        let response = null;
        if (REASON_TRANSACTIONS.includes(row.mcdTransType)) {
          const batchResponse = await postPayoff(row.reason, row.loan, count);
          response = batchResponse['PostTransaction'].data;
        } else {
          response = await axiosCall({ url, method: 'post', data: formData }, config, headers);
        }
        // Check the response and provide feedback
        if (response && response.posted === true) {
          enqueueSnackbar(`Transaction Posted: ${count}`, {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(`Transaction was not Posted: ${count}`, {
            variant: 'error',
          });
        }

        return response;
      } catch (error) {
        console.error('Error Posting new Transaction: ' + error.response.data.message);
        enqueueSnackbar('Error Posting new Transaction. Hover icon in column 2', { variant: 'error' });
        return { errors: error.response.data.message };
      }
    },
    [props.batchNumber, props.token, enqueueSnackbar, get, postPayoff, props.transactionCode],
  );

  // Sale Transfer
  const saleTransfer = useCallback(
    async (row: LoanPaymentRowType) => {
      if (row.from && row.to && row.amount && row.loan) {
        const url = `${process.env.REACT_APP_BATCH_REQUEST}`;
        const controller = get();
        const config = { signal: controller.signal };

        const formData = {
          GroupCode: {
            endpoint: `loan/${row.loan}/servicing`,
            data: {
              investorGroupCode: row.groupCode,
              ownerXferDate: row.ownerXferDate,
            },
          },
          SaleTransfer: {
            endpoint: `${process.env.REACT_APP_CREATE_TRANSACTION.replace('?', props.batchNumber).replace('live/', '')}${row.loan}/transfer`,
            data: {
              fromCode: row.from,
              toCode: row.to,
              amount: row.amount,
            },
          },
        };

        const headers = {
          'Session-Token': props.token,
          'Content-Type': 'application/json',
        };

        try {
          const response = await axiosCall({ url, method: 'post', data: formData }, config, headers);
          if (response) {
            enqueueSnackbar('Transfer Posted ' + row.cnt, {
              variant: 'success',
            });
          } else {
            enqueueSnackbar('Transfer not Posted ' + row.cnt, {
              variant: 'error',
            });
          }
          return response;
        } catch (error) {
          console.error('Error posting transfer: ' + error.response.data.message);
          enqueueSnackbar('Error posting transfer.', { variant: 'error' });
          return { errors: error.response.data.message };
        }
      }
      return null;
    },
    [props.batchNumber, props.token, enqueueSnackbar, get],
  );

  // Save and optionally post a single row. Returns true when the row was posted successfully.
  const runSingleRow = useCallback(
    async (row: LoanPaymentRowType, submit: boolean): Promise<boolean> => {
      if (SALE_TRANSFER_TRANSACTIONS.includes(props.transactionCode)) {
        if (submit) {
          const postResponse = await saleTransfer(row);
          return Boolean(postResponse);
        }
        return false;
      }

      let response: any;
      if (Boolean(row.cnt)) {
        response = await saveTransaction(row);
      } else {
        response = await createTransaction(row);
      }

      let postResponse: any;
      if (response !== undefined && submit) {
        postResponse = await postTransaction(response.cnt, { ...response, reason: row.reason });
      }

      updateRow(row.id, {
        ...row,
        ...response,
        ...postResponse,
      } as LoanPaymentRowType);
      setHasUpdates(false);

      return Boolean(postResponse && postResponse.posted);
    },
    [props.transactionCode, createTransaction, saleTransfer, postTransaction, updateRow, saveTransaction],
  );

  const postRow = useCallback(
    async (row: LoanPaymentRowType) => {
      setPostingRowId(row.id);
      try {
        const posted = await runSingleRow(row, true);
        if (posted) {
          await removeRow(row.total, '', row.id);
        }
      } finally {
        setPostingRowId(null);
      }
    },
    [runSingleRow, removeRow],
  );

  // Create new transactions, save existing transactions, and post based on the "submit" parameter
  const runBatch = useCallback(
    async (submit: boolean) => {
      const postedRows: LoanPaymentRowType[] = [];
      for await (const row of rows) {
        const posted = await runSingleRow(row, submit);
        if (posted) {
          postedRows.push(row);
        }
      }

      // remove transactions that were posted successfully. Omit cnt to delete only from UI
      for await (const postedRow of postedRows) {
        removeRow(postedRow.total, '', postedRow.id);
      }
    },
    [rows, runSingleRow, removeRow],
  );

  // Enable/Disable buttons depending if changes have been made
  // 27 Sale/Transfer transactions cannot be saved. Must be posted immediately
  useEffect(() => {
    setDisableSave(SALE_TRANSFER_TRANSACTIONS.includes(props.transactionCode));
  }, [rows, hasUpdates, props.transactionCode]);

  // Load the batch
  useEffect(() => {
    //this loads the requested batch the user selected
    if (!loadingBatch) {
      setUpRows();
    }
    if (batchErrorMessages) {
      enqueueSnackbar(batchErrorMessages, { variant: 'error' });
    }
  }, [loadingBatch, batchErrorMessages, setUpRows, enqueueSnackbar]);

  // same TR types as Payment Received Date column; only after a batch is retrieved (batchNumber set).
  const showBulkPaymentReceivedDate =
    PAYMENT_RECEIVED_DATE_TRANSACTIONS.includes(props.transactionCode) && Boolean(props.batchNumber);
  // When "Apply to all" is checked, per-row date pickers are disabled until the user unchecks.
  const lockRowLevelPaymentReceivedDates = showBulkPaymentReceivedDate && bulkApplyToAllChecked;

  /** SQC-566: Aggregate (+1/-1) so one LoadingModal covers saved-impound hydration across rows (policy + parcel build). */
  const savedImpoundHydrationLoadingRef = useRef(0);
  const [savedImpoundHydrationLoading, setSavedImpoundHydrationLoading] = useState(false);
  const adjustSavedImpoundHydrationLoading = useCallback((delta: number) => {
    if (!delta) return;
    savedImpoundHydrationLoadingRef.current += delta;
    const next = savedImpoundHydrationLoadingRef.current > 0;
    setSavedImpoundHydrationLoading((prev) => (prev !== next ? next : prev));
  }, []);

  return (
    <div className={Style.tableSection}>
      {savedImpoundHydrationLoading ? <LoadingModal open /> : null}
      {showBulkPaymentReceivedDate ? (
        <div className={Style.tableToolbar}>
          <Typography component='span' variant='caption' className={Style.bulkPaymentDateLabel}>
            Payment Date (all rows):
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={bulkPaymentDate}
              onChange={(v) => {
                setBulkPaymentDate(v);
                setBulkApplyToAllChecked(false);
              }}
              slotProps={{
                textField: {
                  variant: 'standard',
                  margin: 'none',
                  size: 'small',
                  className: Style.bulkPaymentDateField,
                  InputProps: {
                    className: Style.bulkPaymentDateInputRoot,
                  },
                  inputProps: {
                    className: Style.bulkPaymentDateInput,
                  },
                },
              }}
            />
          </LocalizationProvider>
          <FormControlLabel
            className={Style.bulkApplyFormControlLabel}
            disableTypography
            control={
              <Checkbox
                size='small'
                className={Style.bulkApplyCheckbox}
                checked={bulkApplyToAllChecked}
                disabled={rows.length === 0}
                onChange={(_, checked) => {
                  if (rows.length === 0) return;
                  if (!checked) {
                    setBulkApplyToAllChecked(false);
                    return;
                  }
                  const applied = applyPaymentReceivedDateToAllRows();
                  setBulkApplyToAllChecked(applied);
                }}
                inputProps={{ 'aria-label': 'Apply selected payment date to all rows' }}
              />
            }
            label={<span className={Style.bulkApplyLabelText}>Apply to all</span>}
          />
        </div>
      ) : null}
      <div className={Style.tableScrollArea}>
        <div className={Style.tableWrapper}>
          <Table stickyHeader aria-label='sticky table' className={Style.paymentsTable}>
            <TableHead>
              <TableRow key='header'>
                <TableCell align='center' className={Style.utilityHeaderCell}>
                  Actions
                </TableCell>
                <TableCell align='center' className={Style.utilityHeaderCell}>
                  Status
                </TableCell>
                <TableCell align='center' className={`${Style.utilityHeaderCell} ${Style.utilityHeaderCellLast}`}>
                  #
                </TableCell>
                {PAYMENT_RECEIVED_DATE_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Payment Received Date
                  </TableCell>
                ) : (
                  <></>
                )}
                <TableCell align='center' className={Style.headerCell}>
                  Loan Number
                </TableCell>
                {IMPOUND_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Impound
                  </TableCell>
                ) : (
                  <></>
                )}
                {TR_TYPE_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    TR Type
                  </TableCell>
                ) : (
                  <></>
                )}
                <TableCell align='center' className={Style.headerCell}>
                  TR Code
                </TableCell>
                {SUFFIX_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Suffix
                  </TableCell>
                ) : (
                  <></>
                )}
                <TableCell align='center' className={Style.headerCell}>
                  Description
                </TableCell>
                {SUBSIDY_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Subsidy
                  </TableCell>
                ) : (
                  <></>
                )}
                {CHECK_NUMBER_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Check No
                  </TableCell>
                ) : (
                  <></>
                )}
                {MORTGAGE_PAYMENT_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Mortgage Payment
                  </TableCell>
                ) : (
                  <></>
                )}
                {PAYMENT_AMOUNT_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Payment Amount
                  </TableCell>
                ) : (
                  <></>
                )}
                {PRINCIPAL_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Principal
                  </TableCell>
                ) : (
                  <></>
                )}
                {INTEREST_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Interest
                  </TableCell>
                ) : (
                  <></>
                )}
                {EXCESS_FUNDS_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Excess Funds
                  </TableCell>
                ) : (
                  <></>
                )}
                {SUSPENSE_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Suspense
                  </TableCell>
                ) : (
                  <></>
                )}
                {PRINCIPAL_CURTAIL_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Principal Curtailed
                  </TableCell>
                ) : (
                  <></>
                )}
                {ESCROW_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Escrow
                  </TableCell>
                ) : (
                  <></>
                )}
                {LATE_CHARGE_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Late Charges
                  </TableCell>
                ) : (
                  <></>
                )}
                {CORPORATE_ADVANCE_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Corporate Advance
                  </TableCell>
                ) : (
                  <></>
                )}
                {ASSESSMENT_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Assessment
                  </TableCell>
                ) : (
                  <></>
                )}
                {ROW_TOTAL_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Total
                  </TableCell>
                ) : (
                  <></>
                )}
                {UNIT_NO_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Unit No
                  </TableCell>
                ) : (
                  <></>
                )}
                {FEE_NO_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Fee No
                  </TableCell>
                ) : (
                  <></>
                )}
                {FEE_CNT_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Fee Cnt
                  </TableCell>
                ) : (
                  <></>
                )}
                {REASON_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Reason
                  </TableCell>
                ) : (
                  <></>
                )}
                {PAYEE_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Payee
                  </TableCell>
                ) : (
                  <></>
                )}
                {PAYEE2_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Payee 2
                  </TableCell>
                ) : (
                  <></>
                )}
                {PAYEE3_TRANSACTIONS.includes(props.transactionCode) ? (
                  <TableCell align='center' className={Style.headerCell}>
                    Payee 3
                  </TableCell>
                ) : (
                  <></>
                )}
                {SALE_TRANSFER_TRANSACTIONS.includes(props.transactionCode) ? (
                  <>
                    <TableCell align='center' className={Style.headerCell}>
                      Investor Due Date
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Expected UPB
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      To (Investor Code)
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      From (Investor Code)
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Group Code
                    </TableCell>
                  </>
                ) : (
                  <></>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <LoanPaymentRow
                  key={row.id}
                  row={row}
                  index={index}
                  removeRow={removeRow}
                  updateRow={updateRow}
                  onPostRow={postRow}
                  postingRowId={postingRowId}
                  selectedTransType={props.transactionCode}
                  suffixQidList={props.suffixQidList}
                  transactionQidList={props.transactionQidList}
                  token={props.token}
                  disablePaymentReceivedDate={lockRowLevelPaymentReceivedDates}
                  adjustSavedImpoundHydrationLoading={adjustSavedImpoundHydrationLoading}
                />
              ))}
            </TableBody>
          </Table>
          {props.batchNumber ? (
            <div className={Style.tableAddRowBar}>
              <Button
                variant='outlined'
                size='small'
                startIcon={<AddIcon />}
                className={Style.addRowButton}
                onClick={addNewRow}
              >
                Add Transaction
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      <div className={Style.batchFooter}>
        {'Total:'}&nbsp;
        <TextField
          value={total.toFixed(2)}
          disabled={true}
          variant='filled'
          className={Style.blueTextField}
          size='small'
        />
        <Button variant='contained' disabled={disableSave} className={Style.seperation} onClick={() => runBatch(false)}>
          Save Batch
        </Button>
        <Button variant='contained' className={Style.seperation} onClick={() => runBatch(true)}>
          Post Batch
        </Button>
        <Button variant='contained' color='error' className={Style.seperation} onClick={() => deleteBatch()}>
          Delete Batch
        </Button>
      </div>
    </div>
  );
});

export default LoanPaymentTable;
